/* Meem Arabic — Alphabet product behaviour | fixed bundle builder
   Loaded once in layout/theme.liquid, so it also works when Shopify's
   theme editor dynamically re-renders the section. */
(() => {
  'use strict';
  if (customElements.get('meem-alphabet')) return;

  class MeemAlphabet extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.$ = (selector) => this.querySelector(selector);
      this.$$ = (selector) => Array.from(this.querySelectorAll(selector));
      this.on = (node, event, handler) => {
        if (node) node.addEventListener(event, handler, { signal: this.abortController.signal });
      };
      this.form = this.$('.ma-product-form');
      this.variantSelect = this.$('[data-variant]');
      this.baseButton = this.$('[data-base-add]');
      this.bundleButton = this.$('[data-bundle-add]');
      this.stickyButton = this.$('[data-sticky-add]');
      this.extraInputs = this.$$('[data-extra]');
      this.baseToggle = this.$('[data-base]');
      this.dialog = this.$('.ma-dialog');
      this.thumbs = this.$$('[data-thumb]');
      this.thumbsIndex = 0;
      this.busy = false;
      this.cartUncertain = false;

      try {
        this.productData = JSON.parse(this.$('[data-product-data]').textContent);
        if (!this.productData.variants || !this.variantSelect) {
          throw new Error('Product variant data missing');
        }
      } catch (error) {
        console.error('[Meem Alphabet] Product data issue:', error);
        this.showBundleError('This selection could not be loaded. Please refresh the page.');
        return;
      }

      // Always attach listeners before enabling the button.
      this.on(this.form, 'submit', (event) => {
        event.preventDefault();
        this.addToCart(false);
      });
      this.on(this.bundleButton, 'click', () => this.addToCart(true));
      this.on(this.stickyButton, 'click', () => this.addToCart(false));
      this.on(this.variantSelect, 'change', () => {
        this.render();
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variantSelect.value);
        try { window.history.replaceState({}, '', url); } catch (error) { /* embedded preview */ }
      });
      this.extraInputs.forEach((input) => this.on(input, 'change', () => this.render()));
      this.on(this.baseToggle, 'change', () => this.render());
      this.on(this.$('[data-select-all]'), 'click', () => {
        if (!this.baseToggle.disabled) this.baseToggle.checked = true;
        this.extraInputs.forEach((input) => { if (!input.disabled) input.checked = true; });
        this.render();
      });
      this.on(this.$('[data-clear]'), 'click', () => {
        if (!this.baseToggle.disabled) this.baseToggle.checked = false;
        this.extraInputs.forEach((input) => { input.checked = false; });
        this.render();
      });

      const bundleTools = this.$('.ma-bundle-tools');
      if (bundleTools) bundleTools.hidden = false;

      // Product image gallery.
      this.thumbs.forEach((thumb, index) => {
        this.on(thumb, 'click', (event) => { event.preventDefault(); this.showImage(index); });
      });
      if (this.thumbs.length && this.$('.ma-gallery-controls')) {
        this.$('.ma-gallery-controls').hidden = false;
        this.showImage(0);
      }
      this.on(this.$('[data-prev]'), 'click', () => this.showImage(this.thumbsIndex - 1));
      this.on(this.$('[data-next]'), 'click', () => this.showImage(this.thumbsIndex + 1));
      this.$$('[data-zoom-link], [data-sample-zoom]').forEach((link) => {
        this.on(link, 'click', (event) => {
          if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
          event.preventDefault();
          this.zoomTrigger = link;
          const image = this.$('[data-zoom-image]');
          image.src = link.href;
          image.alt = link.querySelector('img')?.alt || 'Enlarged worksheet preview';
          this.dialog.showModal();
          this.syncSticky();
        });
      });
      this.on(this.$('[data-close-zoom]'), 'click', () => this.dialog?.close());
      this.on(this.dialog, 'click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right ||
            event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      this.on(this.dialog, 'close', () => {
        this.zoomTrigger?.focus();
        this.syncSticky();
      });

      // Mobile buy-bar visibility.
      this.buyVisible = true;
      this.belowBuy = false;
      this.bundleVisible = false;
      if ('IntersectionObserver' in window) {
        const buy = this.$('.ma-buy');
        const bundles = this.$('.ma-bundle-section');
        if (buy && bundles) {
          this.visibilityObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.target === buy) {
                this.buyVisible = entry.isIntersecting;
                this.belowBuy = entry.boundingClientRect.bottom < 0;
              } else {
                this.bundleVisible = entry.isIntersecting;
              }
            });
            this.syncSticky();
          }, { threshold: 0 });
          this.visibilityObserver.observe(buy);
          this.visibilityObserver.observe(bundles);
        }
      }
      this.render();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.visibilityObserver?.disconnect();
      this.initialized = false;
    }

    getVariant() {
      return this.productData.variants[String(this.variantSelect.value)];
    }

    selectedExtras() {
      return this.extraInputs.filter((input) => input.checked && !input.disabled);
    }

    money(cents) {
      const currency = this.productData.currency || 'AUD';
      const locale = currency === 'AUD' ? 'en-AU' : (document.documentElement.lang || 'en-AU');
      try {
        const value = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
          .format(cents / 100);
        return value;
      } catch (error) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    showBundleError(message) {
      if (this.bundleButton) {
        this.bundleButton.hidden = false;
        this.bundleButton.disabled = true;
        this.bundleButton.textContent = 'Bundle selection unavailable';
      }
      const status = this.$('[data-bundle-status]');
      if (status) status.textContent = message;
    }

    render() {
      const variant = this.getVariant();
      if (!variant) {
        this.showBundleError('Please select a valid product option.');
        return;
      }
      if (!variant.available || this.productData.requiresPlan) this.baseToggle.checked = false;
      const includeBase = this.baseToggle.checked && variant.available && !this.productData.requiresPlan;
      const selected = this.selectedExtras();
      const count = (includeBase ? 1 : 0) + selected.length;
      const priced = [
        ...(includeBase ? [{title: this.productData.baseTitle, price: Number(variant.priceCents || 0), input: null}] : []),
        ...selected.map(input => ({title: input.dataset.title, price: Number(input.dataset.cents || 0), input}))
      ];
      const variantIds = [
        ...(includeBase ? [String(variant.id)] : []),
        ...selected.map(input => String(input.dataset.id))
      ];
      const qualifies = priced.length === 4 && new Set(variantIds).size === 4;
      const subtotal = priced.reduce((sum, entry) => sum + entry.price, 0);
      const freeItem = qualifies ? priced.reduce((best, entry) => entry.price <= best.price ? entry : best) : null;
      const freeSaving = freeItem ? freeItem.price : 0;
      const totalCents = subtotal - freeSaving;
      this.$('.ma-included').classList.toggle('is-unchecked', !includeBase);
      const caption = this.$('[data-base-caption]');
      if (caption) caption.textContent = includeBase ? 'Selected — untick to remove' : 'Not included — tick to add';

      this.$$('[data-main-price], [data-included-price], [data-sticky-price]')
        .forEach((element) => { element.textContent = variant.price; });
      const compare = this.$('[data-main-compare]');
      const saving = this.$('[data-main-saving]');
      if (compare) { compare.hidden = !variant.compare; compare.textContent = variant.compare || ''; }
      if (saving) { saving.hidden = !variant.saving; saving.textContent = variant.saving || ''; }
      const basePriceElement = this.$('[data-included-price]');
      if (basePriceElement) {
        const freeBase = Boolean(freeItem && !freeItem.input);
        basePriceElement.textContent = freeBase ? 'FREE' : variant.price;
        basePriceElement.classList.toggle('is-free', freeBase);
      }
      this.extraInputs.forEach(input => {
        const label = input.closest('.ma-bundle-row')?.querySelector('.ma-extra-price strong');
        if (!label) return;
        if (!label.dataset.normalPrice) label.dataset.normalPrice = label.textContent;
        const isFree = Boolean(freeItem && freeItem.input === input);
        label.textContent = isFree ? 'FREE' : label.dataset.normalPrice;
        label.classList.toggle('is-free', isFree);
      });
      const offer = this.$('[data-builder-offer]');
      if (offer) {
        offer.textContent = qualifies ? `🎁 Fourth bundle FREE — save ${this.money(freeSaving)} on your collection!`
          : count === 3 ? '🎁 Add the fourth bundle FREE to complete your collection!'
          : 'Choose any 3 bundles and get the fourth FREE.';
        offer.classList.toggle('is-active', qualifies);
      }
      const totalElement = this.$('[data-total]');
      if (totalElement) totalElement.textContent = this.money(totalCents);
      const countElement = this.$('[data-bundle-count]');
      if (countElement) countElement.textContent = `${count} bundle${count === 1 ? '' : 's'}`;
      const list = this.$('[data-selected-list]');
      if (list) {
        list.replaceChildren();
        const choices = [
          ...(includeBase ? [[this.productData.baseTitle, freeItem && !freeItem.input ? 'FREE' : 'Selected']] : []),
          ...selected.map((input) => [input.dataset.title, freeItem && freeItem.input === input ? 'FREE' : 'Selected'])
        ];
        if (!choices.length) {
          const empty = document.createElement('li');
          empty.textContent = 'No bundles selected yet.';
          list.append(empty);
        } else {
          choices.forEach(([title, label]) => {
            const li = document.createElement('li');
            li.append(document.createTextNode(title));
            const span = document.createElement('span');
            span.textContent = label;
            if (label === 'FREE') span.classList.add('is-free');
            li.append(span);
            list.append(li);
          });
        }
      }

      const working = this.busy || this.cartUncertain;
      const baseUnavailable = !variant.available || this.productData.requiresPlan;
      [this.baseButton, this.stickyButton].forEach((button) => {
        if (button) button.disabled = working || baseUnavailable;
      });
      if (this.bundleButton) this.bundleButton.disabled = working || count === 0;
      this.baseToggle.disabled = working || baseUnavailable;
      if (this.bundleButton) {
        this.bundleButton.hidden = false;
        this.bundleButton.textContent = this.busy ? 'Adding to cart…' :
          this.cartUncertain ? 'Please check your cart' :
          count === 0 ? 'Select at least one bundle' :
          `Add ${count} bundle${count === 1 ? '' : 's'} to cart ↗`;
      }
      if (this.baseButton) {
        this.baseButton.textContent = this.busy ? 'Adding to cart…' :
          this.cartUncertain ? 'Please check your cart' :
          !variant.available ? 'Sold out' : 'Add Alphabet Bundle to cart ↗';
      }
      if (this.stickyButton) this.stickyButton.textContent = this.busy ? 'Adding…' : 'Add Alphabet only ↗';
      if (this.variantSelect) this.variantSelect.disabled = this.busy;
      const tools = this.$$('[data-select-all], [data-clear]');
      tools.forEach((tool) => { tool.disabled = this.busy; });
      const fieldset = this.$('.ma-bundle-options');
      if (fieldset) fieldset.disabled = this.busy;
    }

    syncSticky() {
      const sticky = this.$('.ma-sticky');
      if (!sticky) return;
      sticky.hidden = !(this.belowBuy && !this.buyVisible && !this.bundleVisible && !this.dialog?.open);
    }

    showImage(index) {
      if (!this.thumbs.length) return;
      this.thumbsIndex = (index + this.thumbs.length) % this.thumbs.length;
      const thumb = this.thumbs[this.thumbsIndex];
      const main = this.$('.ma-main-image');
      const link = this.$('[data-zoom-link]');
      if (main && link) {
        main.removeAttribute('srcset');
        main.src = thumb.dataset.src;
        main.alt = thumb.dataset.alt;
        link.href = thumb.href;
      }
      this.thumbs.forEach((item, i) => {
        if (i === this.thumbsIndex) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      const counter = this.$('[data-image-counter]');
      if (counter) counter.textContent = `${this.thumbsIndex + 1} / ${this.thumbs.length}`;
      const rail = this.$('.ma-thumbs');
      if (rail) rail.scrollLeft = Math.max(0,
        thumb.offsetLeft - rail.offsetLeft - rail.clientWidth / 2 + thumb.clientWidth / 2);
    }

    async addToCart(includeExtras) {
      const variant = this.getVariant();
      if (!variant || this.busy || this.cartUncertain) return;
      if (!includeExtras && (!variant.available || this.productData.requiresPlan)) return;
      const includeBase = !includeExtras || (this.baseToggle.checked && variant.available && !this.productData.requiresPlan);
      const ids = [...(includeBase ? [variant.id] : []), ...(includeExtras ? this.selectedExtras().map((input) => input.dataset.id) : [])]
        .map(String).filter((id) => /^\d+$/.test(id));
      if (!ids.length) {
        this.showBundleError('No valid products were selected.');
        return;
      }
      const items = Array.from(new Set(ids)).map((id) => ({ id: Number(id), quantity: 1 }));
      this.$$('.ma-inline-status').forEach((message) => { message.textContent = ''; });
      this.$$('[data-cart-help]').forEach((element) => { element.hidden = true; });
      const status = this.$(includeExtras ? '[data-bundle-status]' : '[data-base-status]');
      this.busy = true;
      this.render();
      if (status) status.textContent = 'Adding your selection…';

      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 20000);
      try {
        // Keep digital worksheet quantities at one; read the live cart before adding.
        const cartResponse = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
          credentials: 'same-origin', headers: { 'Accept': 'application/json' }, signal: controller.signal
        });
        if (!cartResponse.ok) throw new Error('Unable to check your cart. Please try again.');
        const liveCart = await cartResponse.json();
        const existing = new Set((liveCart.items || []).map(item => String(item.variant_id)));
        const missingItems = items.filter(item => !existing.has(String(item.id)));
        if (missingItems.length) {
          const response = await fetch(this.dataset.addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: missingItems }),
            signal: controller.signal
          });
          let payload;
          try { payload = await response.json(); } catch (_) { throw new Error('Could not confirm your cart update.'); }
          if (!response.ok) throw new Error(typeof payload.description === 'string' ? payload.description : 'Unable to add these bundles.');
        }
        this.cartUncertain = false;
        if (status) status.textContent = 'Added to your cart.';
        document.dispatchEvent(new CustomEvent('meem:cart-updated'));
        if (window.MeemDrawer && typeof window.MeemDrawer.open === 'function') {
          await window.MeemDrawer.open();
        } else if (status) {
          status.textContent = 'Added to your cart. Select the cart icon at the top to review.';
        }
      } catch (error) {
        this.cartUncertain = true;
        if (status) status.textContent = `${error.message || 'The request could not be confirmed.'} Please check your cart before trying again.`;
        this.$$('[data-cart-help]').forEach((element) => { element.hidden = false; });
      } finally {
        window.clearTimeout(timer);
        this.busy = false;
        this.render();
      }
    }
  }

  customElements.define('meem-alphabet', MeemAlphabet);
})();
